import React, { useEffect, useState } from 'react';
import { snakeCase, keys, isNil, get, find, isEmpty, size } from 'lodash';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Container,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import { ArrowDropDown as Down, ArrowDropUp as Up } from '@mui/icons-material';
import PropTypes from 'prop-types';
import Split from 'react-split';
import { change, reduxForm, reset } from 'redux-form';
import { useSelector } from 'react-redux';
import { Button } from '../../../../common/components/Button/Button';
import { Dialog } from '../../../../common/components/Dialog/Dialog';
import Loader from '../../../../common/components/Loader/Loader';
import MappedLabel from '../../../../common/components/MappedLabel/MappedLabel';
import CommonNavigationPrompt from '../../../../common/components/NavigationPrompt/component';
import { isMobile, isOnlyMobile } from '../../../../common/services/isMobile';
import { Toolbar } from '../../../../common/components/Toolbar/Toolbar';
import {
  getCurrentValue,
  getProcessedDiff,
  getProcessedStatus,
  getUpdatedFields,
  getUpdatedParameters,
  humanizeName,
} from './ChargerConfigurationHelper';
import { ChargerConfigurationModifiedNodeIndicator } from './components/ChargerConfigurationModifiedNodeIndicator';
import ConfigurationField, { hintComponent } from './components/ConfigurationField';
import { ConfigurationTooltipWrapper } from './components/ConfigurationTooltipWrapper';
import { chargerConfigurationFormId, defaultGroupName } from './constants';
import { getSelectedChargerIds } from '../../../ConfigUpdate/store/selectors';

const gutterStyle = {
  background: 'linear-gradient(0.25turn, rgb(175, 175, 175), rgb(250, 250, 250))',
  width: '4px',
};

const cardContentStyle = {
  marginBottom: 15,
};

const containerStyle = {
  maxWidth: 'unset',
  paddingLeft: 5,
  paddingRight: 5,
};

export const processPackagesFieldName = (nodeName, parameterName) => `${snakeCase(nodeName)}/${snakeCase(parameterName)}`;

export const processPackagesFieldMappingName = (nodeName, parameterName) => `${nodeName}/${parameterName}`;

function ChargerConfigurationComponent(props) {
  const [expanded, setExpanded] = useState('');
  const selectedChargersIds = useSelector(getSelectedChargerIds);
  const getChargerId = () => {
    const {
      currentCharger: { c_uuid: chargerUuid },
    } = props;

    return chargerUuid;
  };

  useEffect(() => {
    if (!props.isBatchUpdate) props.closeDialog();

    const beforeUnloadHandler = () => {
      const { unlockConfig, isModal, lockInterval } = props;
      const chargerId = isModal ? selectedChargersIds : getChargerId();

      clearInterval(lockInterval);
      unlockConfig(chargerId);
    };

    window.addEventListener('beforeunload', beforeUnloadHandler);

    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);

      beforeUnloadHandler();
    };
  }, []);

  const cancelChanges = (fieldName, value = '') => {
    props.dispatch(change(chargerConfigurationFormId, fieldName, value));
  };

  const onFieldChange = () => {
    if (!props.isBatchUpdate) {
      if (props.isLocked) return;

      props.lockChargerConfiguration(getChargerId());
    }
  };

  const renderModuleFields = () => {
    const {
      config: { ui_labels: uiLabels },
      configuration,
      selectedModule,
      resetField,
    } = props;

    const node = find(configuration.nodes, { node_name: selectedModule });

    if (isEmpty(selectedModule) || !node) {
      return <div className="charger-configuration-help-message">Select a module on the left.</div>;
    }

    return node.groups.map((item) => {
      const groupFields = item.parameters.map((parameter) => {
        const fieldName = processPackagesFieldName(node.node_name, parameter.parameter_name);
        const labelText = get(
          uiLabels,
          processPackagesFieldMappingName(node.node_name, parameter.parameter_name),
          parameter.parameter_name,
        );

        return (
          <ConfigurationField
            key={parameter.parameter_name}
            labelText={labelText}
            parameter={parameter}
            fieldName={fieldName}
            resetField={() => resetField(node.node_name, parameter.parameter_name)}
            cancelChanges={() => {
              cancelChanges(fieldName, parameter.value);
              if (props.isBatchUpdate) {
                props.onBatchFieldClear(fieldName);
              }
            }}
            onFieldChange={onFieldChange}
            readOnly={props.isReadOnly}
            isBatchUpdate={props.isBatchUpdate}
            onBatchFieldChange={() => props.onBatchFieldChange(fieldName)}
            isModal={props.isModal}
          />
        );
      });

      const groupHeader = (
        <div className="group-header">
          <div>{humanizeName(item.group_name === 'default' ? defaultGroupName : item.group_name)}</div>
          {item.description && <ConfigurationTooltipWrapper trigger={hintComponent} message={item.description} />}
        </div>
      );

      return (
        <Card key={item.group_name}>
          <CardHeader title={groupHeader} />
          <CardContent style={cardContentStyle}>{groupFields}</CardContent>
        </Card>
      );
    });
  };

  const renderMobileModuleList = () =>
    props.configuration.nodes.map(({ node_name: nodeName }) => (
      <Accordion key={nodeName} expanded={nodeName === expanded} onChange={() => setExpanded(expanded ? '' : nodeName)}>
        <AccordionSummary onClick={() => props.selectChargerConfigurationModule(nodeName)}>
          <Box display="flex" justifyContent="space-between" flexGrow={1}>
            {humanizeName(nodeName)}
            <IconButton style={{ padding: 0 }}>{nodeName === expanded ? <Up /> : <Down />}</IconButton>
          </Box>
        </AccordionSummary>
        <AccordionDetails>{renderModuleFields()}</AccordionDetails>
      </Accordion>
    ));

  const renderModulesList = (diff) => {
    const {
      selectedModule,
      configurationForm: { initial, values },
      config: { ui_labels: uiLabels },
      fieldsPathMap,
    } = props;

    const pocessedDiff = getProcessedDiff(diff, fieldsPathMap, values);

    const updatedNodes = {};

    keys(pocessedDiff).forEach((parameterPath) => {
      const updatedNode = fieldsPathMap[parameterPath];

      if (isNil(updatedNode)) return;

      const updatedNodeName = updatedNode.node;

      const labelText = get(uiLabels, parameterPath, updatedNode.defaultName);

      const nodeInfo = {
        name: labelText,
        value: getCurrentValue(updatedNode.controller_type, updatedNode.available_values, values[parameterPath]),
        currentValue: getCurrentValue(updatedNode.controller_type, updatedNode.available_values, updatedNode.value),
        status: getProcessedStatus(
          props.isBatchUpdate
            ? pocessedDiff[parameterPath] !== initial[parameterPath] || props.anyTouched
            : pocessedDiff[parameterPath] !== initial[parameterPath],
          updatedNode.status,
        ),
        controller_type: updatedNode.controller_type,
        available_values: updatedNode.available_values,
      };

      updatedNodes[updatedNodeName] = (updatedNodes[updatedNodeName] || []).concat([nodeInfo]);
    });

    return (
      <List component="nav">
        {props.configuration.nodes.map(({ node_name: nodeName }) => {
          const updatedModuleFields = updatedNodes[nodeName];

          return (
            <ListItem
              button
              selected={nodeName === selectedModule}
              onClick={() => props.selectChargerConfigurationModule(nodeName)}
              key={nodeName}
            >
              <ListItemText primary={<MappedLabel>{nodeName}</MappedLabel>} />
              <ListItemSecondaryAction>
                <ChargerConfigurationModifiedNodeIndicator
                  nodeName={<MappedLabel>{nodeName}</MappedLabel>}
                  updatedModuleFields={updatedModuleFields}
                  isModal={props.isModal}
                />
              </ListItemSecondaryAction>
            </ListItem>
          );
        })}
      </List>
    );
  };

  const onConfigurationSubmit = (diff) => {
    const { fieldsPathMap, lockInterval } = props;

    const updatedParameters = getUpdatedParameters(diff, fieldsPathMap);

    props.updateChargerConfiguration(getChargerId(), updatedParameters, lockInterval);
  };

  const renderConfirmationTable = (diff) => {
    const {
      fieldsPathMap,
      config: { uiLabels },
      configurationForm: { initial },
    } = props;

    return (
      <div>
        <Table stickyHeader={!isOnlyMobile} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell align="left">Node Name {'>'} Parameter Name</TableCell>
              <TableCell align="left">Old Value</TableCell>
              <TableCell align="left">New Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(diff).map((row) => (
              <TableRow key={row}>
                <TableCell
                  data-cell="Node Name > Parameter Name"
                  component={isOnlyMobile ? 'td' : 'th'}
                  scope={isOnlyMobile ? 'column' : 'row'}
                >
                  {`${humanizeName(get(fieldsPathMap, `${row}.node`))} > ${get(uiLabels, row, get(fieldsPathMap, `${row}.defaultName`))}`}
                </TableCell>
                <TableCell data-cell="Old Value" align="left">
                  {getCurrentValue(
                    get(fieldsPathMap, `${row}.controller_type`),
                    get(fieldsPathMap, `${row}.available_values`),
                    initial[row],
                  )}
                </TableCell>
                <TableCell data-cell="New Value" align="left">
                  {getCurrentValue(get(fieldsPathMap, `${row}.controller_type`), get(fieldsPathMap, `${row}.available_values`), diff[row])}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  const {
    fieldsPathMap,
    configuration,
    handleSubmit,
    lockInterval,
    isDialogOpen,
    closeDialog,
    configurationForm: { initial, values, fields },
    isFetching,
    online,
    fullState,
    isBatchUpdate,
  } = props;
  const nodes = get(configuration, 'nodes');
  const error = get(configuration, 'error');
  const showStub = !size(nodes);

  const diff = getUpdatedFields(initial, values, isBatchUpdate ? fields : null);

  const showRebootMessage = Object.keys(diff).some((row) => {
    const groupName = get(fieldsPathMap, `${row}.node`);
    const node = find(nodes, { node_name: groupName });

    if (!node) {
      return false;
    }

    const actionOnChange = get(node, 'action_on_change.action_apply_type', 'AUTO');

    return (
      actionOnChange === 'MANUAL' ||
      !online ||
      fullState.some((state) => state.connector_id !== 0 && state.connector_ev_connection === 'connected')
    );
  });

  const actions = [
    <Button color="secondary" key="cancel-btn" disabled={isFetching} onClick={() => closeDialog()}>
      Cancel
    </Button>,
    <Button key="submit-btn" color="primary" disabled={isFetching} onClick={() => onConfigurationSubmit(diff)}>
      Submit
    </Button>,
  ];

  const lockedPopupActions = [
    <Button key="submit-btn" color="primary" onClick={() => props.toggleLockedPopup(false)}>
      Ok
    </Button>,
  ];

  return (
    <>
      <form id="chargerConfiguration" onSubmit={handleSubmit} style={{ height: '100%' }}>
        {error ? (
          <div className="empty-configuration-message">{error}</div>
        ) : showStub ? (
          <div className="empty-configuration-message">The device did not report any configurable parameters.</div>
        ) : isMobile ? (
          renderMobileModuleList()
        ) : (
          <Split className="charger-configuration" sizes={[20, 80]} gutterSize={4} gutterStyle={() => gutterStyle}>
            <div className="charger-configuration-pane">{renderModulesList(diff)}</div>
            <div className="charger-configuration-pane charger-configuration-field-pane dark-background">
              <Container maxWidth="lg" style={containerStyle}>
                {renderModuleFields()}
              </Container>
            </div>
          </Split>
        )}
      </form>
      <Dialog actions={actions} modal open={isDialogOpen} fullWidth>
        {isFetching && <Loader />}
        <div className={`confirmation-dialog-text ${isFetching ? 'is-fetching' : ''}`}>
          {renderConfirmationTable(diff)}
          {showRebootMessage && (
            <div className="configuration-confirmation-meassage-underline">
              The device needs to be rebooted after at least one of the parameters becomes Applied.
            </div>
          )}
          {props.submitError && <div className="confirmation-dialog-warning">{props.submitError}</div>}
        </div>
      </Dialog>
      <Dialog actions={lockedPopupActions} modal open={props.showLockedPopup} fullWidth>
        <div className="confirmation-dialog-text">
          The Configuration parameters are not available for editing as they are pending changes.
        </div>
      </Dialog>
      <CommonNavigationPrompt
        when={size(diff) > 0}
        customOnConfirm={() => {
          props.dispatch(reset(chargerConfigurationFormId));
          clearInterval(lockInterval);
        }}
      />
    </>
  );
}

ChargerConfigurationComponent.propTypes = {
  configuration: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  isReadOnly: PropTypes.bool,
  selectedModule: PropTypes.string,
  handleSubmit: PropTypes.func,
  fieldsPathMap: PropTypes.object,
  selectChargerConfigurationModule: PropTypes.func,
};

const ChargerConfigurationForm = reduxForm({
  form: chargerConfigurationFormId,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  destroyOnUnmount: false,
})(ChargerConfigurationComponent);

export default ChargerConfigurationForm;
